/**
 * RMA hand-off from a freshly created ticket — the compact context the New
 * Ticket page passes to the RMA form through router navigation state.
 *
 * Navigation state is structured-cloned by the browser, so the joined
 * structure entity (unit, relays, loads, schedules, alarm states…) is not
 * hand-off material: one value the browser cannot clone aborts the navigation
 * after the ticket already exists. Only the fields the RMA form reads travel.
 *
 * Contains:
 * - buildRmaHandoff — the `fromTicket` object CreateReturnForm rehydrates from
 */

/**
 * Read one field from an Immutable Map or a plain object.
 *
 * @param {object | null | undefined} source
 * @param {string} key
 * @param {*} [fallback]
 * @returns {*}
 */
function readField(source, key, fallback = null) {
  if (!source) return fallback;
  const value = typeof source.get === 'function' ? source.get(key, fallback) : source[key];
  return value === undefined ? fallback : value;
}

/**
 * The radio identity the RMA form needs: the id it submits and the ICCID it
 * displays in the returned-device lookup box.
 *
 * @param {object | null | undefined} radio
 * @returns {{ id: number, iccid: string } | null}
 */
function slimRadio(radio) {
  if (!radio) return null;
  return { id: readField(radio, 'id'), iccid: readField(radio, 'iccid', '') };
}

/**
 * A device container (relay controller, CIC, Z-Wave controller) reduced to
 * the radio hanging off it, or null when the site has none of that kind.
 *
 * @param {object | null | undefined} device
 * @returns {{ radio: { id: number, iccid: string } } | null}
 */
function slimDevice(device) {
  const radio = slimRadio(readField(device, 'radio'));
  return radio ? { radio } : null;
}

/**
 * The customer, site group and site as the RMA form shows and submits them:
 * ids for the payload, names for the summary rows and site link.
 *
 * @param {object | null | undefined} entity
 * @returns {{ id: number, name: string, description: string } | null}
 */
function slimEntity(entity) {
  if (!entity) return null;
  return {
    id: readField(entity, 'id'),
    name: readField(entity, 'name', ''),
    description: readField(entity, 'description', ''),
  };
}

/**
 * The customer plus the billboard flag that picks the RMA form's vocabulary
 * (Plant / Structure versus Site Group / Site).
 *
 * @param {object | null | undefined} customer
 * @returns {{ id: number, name: string, description: string, is_billboard: boolean } | null}
 */
function slimCustomer(customer) {
  const entity = slimEntity(customer);
  return entity ? { ...entity, is_billboard: readField(customer, 'is_billboard', true) } : null;
}

/**
 * Build the `fromTicket` navigation state for the RMA form.
 *
 * Every value is a plain, structured-cloneable object. The ticket is the
 * create-endpoint echo (already plain JSON), optionally carrying the contact
 * picked on the form so the RMA can pre-fill its reporting contact.
 *
 * @param {{
 *   customer: object | null,
 *   siteGroup: object | null,
 *   site: object | null,
 *   ticket: object | null,
 *   selectedContact: object | null,
 *   payload: { problem_description?: string, issueCategory_id?: number, issue_id?: number },
 * }} context
 * @returns {object} the object CreateReturnForm reads as `location.state.fromTicket`
 */
export function buildRmaHandoff({ customer, siteGroup, site, ticket, selectedContact, payload }) {
  const slimSite = slimEntity(site);
  return {
    customer: slimCustomer(customer),
    siteGroup: slimEntity(siteGroup),
    site: slimSite
      ? {
          ...slimSite,
          relay_controller: slimDevice(readField(site, 'relay_controller')),
          cic: slimDevice(readField(site, 'cic')),
          z_wave_controller: slimDevice(readField(site, 'z_wave_controller')),
        }
      : null,
    ticket: selectedContact ? { ...ticket, contact: selectedContact } : ticket,
    problemDescription: payload.problem_description ?? '',
    issueCategoryId: payload.issueCategory_id ?? null,
    issueId: payload.issue_id ?? null,
  };
}
